import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Input, Select, Hidden } from '~/components/Form';
import { storeMedidor, updateMedidor } from '~/store/modules/medidor/actions';
import { Spinner } from '~/components';
import { t } from '~/helpers';

import { Container } from './styles';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const optionsLBC = useMemo(
    () => [
      { value: 1, label: 'Liberado' },
      { value: 2, label: 'Bloqueado' },
      { value: 3, label: 'Cancelado' },
    ],
    []
  );

  const { loading, find, params } = useSelector((state) => state.medidor);
  const initials = create ? data : { ...find, ...data };

  const handleSubmit = useCallback(
    async (param) => {
      try {
        const schema = Yup.object().shape({
          medi_descricao: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(2, t('s.geral.validacao.tamanhominimo').replace('%s', 2))
            .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20)),
          medi_multiplicador: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.campoobrigatorio').replace('%s', 1))
            .max(9999, t('s.geral.validacao.tamanhomaximo').replace('%s', 9999))
            .positive(t('s.geral.validacao.numeronegativo')),
          medi_divisor: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.campoobrigatorio').replace('%s', 1))
            .max(9999, t('s.geral.validacao.tamanhomaximo').replace('%s', 9999))
            .positive(t('s.geral.validacao.numeronegativo')),
          medi_multiplicador: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.campoobrigatorio').replace('%s', 1))
            .max(9999, t('s.geral.validacao.tamanhomaximo').replace('%s', 9999))
            .positive(t('s.geral.validacao.numeronegativo')),
          medi_fator_multiplicacao: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.campoobrigatorio').replace('%s', 1))
            .max(9999, t('s.geral.validacao.tamanhomaximo').replace('%s', 9999))
            .positive(t('s.geral.validacao.numeronegativo')),
          medi_diametro: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.campoobrigatorio').replace('%s', 1))
            .max(9999, t('s.geral.validacao.tamanhomaximo').replace('%s', 9999))
            .positive(t('s.geral.validacao.numeronegativo')),
          medi_vazao_maxima: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.campoobrigatorio').replace('%s', 1))
            .max(9999, t('s.geral.validacao.tamanhomaximo').replace('%s', 9999))
            .positive(t('s.geral.validacao.numeronegativo')),
          medi_vazao_minima: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.campoobrigatorio').replace('%s', 1))
            .max(9999, t('s.geral.validacao.tamanhomaximo').replace('%s', 9999))
            .positive(t('s.geral.validacao.numeronegativo')),
          medi_erro_maximo: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.campoobrigatorio').replace('%s', 1))
            .max(9999, t('s.geral.validacao.tamanhomaximo').replace('%s', 9999))
            .positive(t('s.geral.validacao.numeronegativo')),
          medi_casas_dec_litro: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.campoobrigatorio').replace('%s', 1))
            .max(9999, t('s.geral.validacao.tamanhomaximo').replace('%s', 9999))
            .positive(t('s.geral.validacao.numeronegativo')),
          medi_casas_dec_encerrante: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.campoobrigatorio').replace('%s', 1))
            .max(9999, t('s.geral.validacao.tamanhomaximo').replace('%s', 9999))
            .positive(t('s.geral.validacao.numeronegativo')),
          medi_status: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .nullable(),
        });

        await schema.validate(param, {
          abortEarly: false,
        });

        const objSend = {
          medi_codigo: '',
          ...initials,
          ...param,
        };

        if (create) dispatch(storeMedidor(objSend, params));
        else dispatch(updateMedidor(objSend, params));
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          reference.current.setErrors(validationErrors);
        }
      }
    },
    [initials, create]
  );

  return (
    <Container>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Hidden name="medi_codigo" label="medi_codigo" />

          <Input name="medi_descricao" label="Descrição" />

          <Input
            name="medi_multiplicador"
            label="Multiplicador"
            type="number"
          />

          <Input name="medi_divisor" label="Divisor" type="number" />

          <Input
            name="medi_fator_multiplicacao"
            label="Fator Multiplicação"
            type="number"
          />

          <Input name="medi_diametro" label="Diametro" type="number" />

          <Input name="medi_vazao_maxima" label="Vazão Máxima" type="number" />

          <Input name="medi_vazao_minima" label="Vazão Mínima" type="number" />

          <Input name="medi_erro_maximo" label="Erro Máximo" type="number" />

          <Input
            name="medi_casas_dec_litro"
            label="Casas Dec. Litro"
            type="number"
          />

          <Input
            name="medi_casas_dec_encerrante"
            label="Casas Dec. Encerrante"
            type="number"
          />

          <Select
            name="medi_status"
            variant="outlined"
            label="Status"
            options={optionsLBC}
          />
        </Form>
      )}
    </Container>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
